import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styles from './LeftMenu.module.css'

import {
    Menu,
    LayoutDashboard,
    Receipt,
    ClipboardList,
    History,
    Coins,
    Tag,
    Users,
    UserCheck,
    Star,
    CalendarDays,
    ListChecks,
    Package,
    Building2,
    UsersRound,
    FileText,
    Sparkles,
    Megaphone,
    Target,
    BarChart2,
    TrendingUp,
    CreditCard,
    Settings2,
    SlidersHorizontal,
    ShieldCheck,
    Zap,
    Bell,
    ArrowLeftRight,
    LogOut,
    WalletCards,
    Link2,
    Banknote,
    Bot,
    UserMinus,
    UserCog,
    BookOpen,
    CalendarCheck,
    CalendarX2,
    Wrench,
    RefreshCw,
    Shield,
    Layers,
    Gift,
    Umbrella,
    PartyPopper,
    ChevronDown,
    ChevronRight,
} from 'lucide-react'

import AuthContext from '../../store/auth-context'
import ActiveStoreSelector from './ActiveStoreSelector'
import ActiveStoreContext from '../../store/ActiveStoreContext'

const ICON_SIZE = 17
const ICON_STROKE = 1.75

const LeftMenu = (props) => {
    const { isOpen, toggler } = props
    const [isPayrollLaunched, setIsPayrollLaunched] = useState(false)
    const [payrollSetupOpen, setPayrollSetupOpen] = useState(false)
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)

    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)

    // Responsive icon size based on screen width
    const [iconSize, setIconSize] = useState(ICON_SIZE)

    useEffect(() => {
        const updateIconSize = () => {
            const width = window.innerWidth
            if (width <= 360) {
                setIconSize(Math.max(12, ICON_SIZE - 5))
            } else if (width <= 480) {
                setIconSize(Math.max(13, ICON_SIZE - 4))
            } else if (width <= 768) {
                setIconSize(Math.max(14, ICON_SIZE - 3))
            } else {
                setIconSize(ICON_SIZE)
            }
        }

        updateIconSize()
        window.addEventListener('resize', updateIconSize)
        return () => window.removeEventListener('resize', updateIconSize)
    }, [])

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768)
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    // Screens with their own fixed bottom action bar (e.g. GIInterfaceAlpha's
    // Save / Save & Preview row) dispatch this so the floating hamburger FAB
    // parks top-left instead of bottom-right, where it'd otherwise sit on
    // top of those buttons. Self-registering at the source of the conflict
    // rather than a hardcoded list of routes here that could drift stale.
    const [hasFixedBottomBar, setHasFixedBottomBar] = useState(false)

    useEffect(() => {
        const onFixedBottomBarChange = (e) => setHasFixedBottomBar(!!e.detail?.active)
        window.addEventListener('layout:fixedBottomBar', onFixedBottomBarChange)
        return () => window.removeEventListener('layout:fixedBottomBar', onFixedBottomBarChange)
    }, [])

    // Same self-registering convention as above, for screens that need the
    // FAB gone entirely rather than just repositioned — e.g. the campaign
    // wizard, which already has its own fixed elements at both the top
    // (header) and bottom (step nav / Launch Now) of the screen, so neither
    // FAB corner is actually free there.
    const [isFabHidden, setIsFabHidden] = useState(false)

    useEffect(() => {
        const onHideFabChange = (e) => setIsFabHidden(!!e.detail?.active)
        window.addEventListener('layout:hideFab', onHideFabChange)
        return () => window.removeEventListener('layout:hideFab', onHideFabChange)
    }, [])

    const activeStore = (() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== "")
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch { return null }
    })()

    const [isStoreSelectorOpen, setIsStoreSelectorOpen] = useState(false)
    const storeSelectorHandler = (k) => {
        setIsStoreSelectorOpen(false)
        asCtx.changeStore(k)
    }

    useEffect(() => {
        if (activeStore === null) {
            setIsStoreSelectorOpen(true)
        }
    }, [activeStore])

    const checkActivePage = (p, q, r, s) => {
        const pathSegments = window.location?.pathname.split("/").filter(Boolean)
        if (p && p !== pathSegments[0]) return false
        if (q && q !== pathSegments[1]) return false
        if (r && r !== pathSegments[2]) return false
        if (s && s !== pathSegments[3]) return false
        return true
    }

    // checkActivePage only checks the segments it's given, so checkActivePage('communications')
    // matches every /communications/* route — fine for a section-wide check, wrong for a nav item
    // meant to represent exactly one page (e.g. the Hub landing page itself).
    const isExactPath = (...segments) => {
        const pathSegments = window.location?.pathname.split("/").filter(Boolean)
        return pathSegments.length === segments.length && segments.every((seg, i) => seg === pathSegments[i])
    }

    // Everything reachable from the Campaigns list — the list itself, a single
    // campaign's detail/wizard, and its wallet — should highlight "Campaigns".
    const isCampaignsSection = () => {
        const pathSegments = window.location?.pathname.split("/").filter(Boolean)
        return pathSegments[0] === 'communications' && ['all-campaigns', 'campaign', 'store-wallet'].includes(pathSegments[1])
    }

    const navigatorData = JSON.parse(authCtx?.loginData || '{}')?.navigatorData || []
    const hasReportsAccess = navigatorData?.includes('/collection-reports') || navigatorData?.includes('/expenses-report') || navigatorData?.includes('/outstanding-report')
    const hasPromoAccess = navigatorData?.includes('/communications') || navigatorData?.includes('/communications/all-campaigns') || navigatorData?.includes('/communications/audience/all-audiences')
    const hasAnalyticsAccess = navigatorData?.includes('/analytics/staff')

    /* ── Shared icon colour helper ── */
    const iconColor = (active) => active ? '#c4b5fd' : 'rgba(148, 163, 184, 0.8)'

    const handleNavClick = (e) => {
        if (isMobile && e.target.closest('a')) {
            toggler()
        }
    }

    return (
        <>
        {isMobile && isOpen && (
            <div className={styles.mobileBackdrop} onClick={toggler} />
        )}
        {isMobile && !isOpen && !isFabHidden && (
            <div
                className={hasFixedBottomBar ? styles.mobileFloatingHamTopLeft : styles.mobileFloatingHam}
                onClick={toggler}
                role="button"
                aria-label="Open menu"
            >
                <Menu size={22} strokeWidth={2} color="rgba(255, 255, 255, 0.92)" />
            </div>
        )}
        {/* Rendered outside .mainWrapper on purpose: on mobile that drawer is
            translated off-screen (transform: translateX(-110%)) while
            closed, and a CSS transform on an ancestor forces this modal's
            position:fixed to resolve against THAT box instead of the
            viewport — so it would get dragged off-screen with the drawer,
            hiding the one screen the app can't function without right
            after login. Keeping it as a sibling here means it's always
            anchored to the real viewport regardless of the drawer's state. */}
        {isStoreSelectorOpen &&
            <ActiveStoreSelector
                isOpen={isStoreSelectorOpen}
                onClose={() => setIsStoreSelectorOpen(false)}
                onSuccess={storeSelectorHandler}
            />
        }
        <div className={`${styles.mainWrapper} ${isMobile && isOpen ? styles.drawerOpen : ''}`} id="leftMenuId">

            {/* ── Logo / Hamburger ── */}
            <div className={styles.hamLogoWrapper}>
                <div className={styles.menuIcon} onClick={() => toggler()}>
                    <Menu size={iconSize + 3} strokeWidth={2} />
                </div>
                {isOpen && (
                    <div className={styles.logoWrapper}>
                        <img src="https://d26wg3m90fktzo.cloudfront.net/uploads/001721807041000002/1775304991393-lokaci-pro-logo-rect.png" alt="Lokaci Pro" />
                    </div>
                )}
            </div>

            {/* ── Navigation ── */}
            <div className={styles.allNavOptionsWrapper} onClick={handleNavClick}>

                {/* Overview */}
                {navigatorData.includes('/overview') && (
                    <Link
                        to="/overview"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Overview' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('overview') ? styles.activeNav : ''}`}>
                            <LayoutDashboard size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('overview'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Overview</span></>}
                        </div>
                    </Link>
                )}

                {/* Create Bill */}
                <Link
                    to="/invoice/create"
                    className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                    title={!isOpen ? 'Create Bill' : ''}
                >
                    <div className={`${styles.eachNavContainer} ${checkActivePage('invoice') ? styles.activeNav : ''}`}>
                        <Receipt size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('invoice'))} />
                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Create Bill</span></>}
                    </div>
                </Link>

                {/* Bookings History */}
                {navigatorData.includes('/bookings') && (
                    <Link
                        to="/bookings"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Bookings History' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('bookings') ? styles.activeNav : ''}`}>
                            <History size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('bookings'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Bookings History</span></>}
                        </div>
                    </Link>
                )}

                {/* Expenses */}
                <Link
                    to="/expenses"
                    className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                    title={!isOpen ? 'Manage Expenses' : ''}
                >
                    <div className={`${styles.eachNavContainer} ${checkActivePage('expenses') ? styles.activeNav : ''}`}>
                        <Coins size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('expenses'))} />
                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Manage Expenses</span></>}
                    </div>
                </Link>

                {/* Deals */}
                {navigatorData.includes('/deals') && (
                    <Link
                        to="/deals"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Deals' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('deals') ? styles.activeNav : ''}`}>
                            <Tag size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('deals'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Deals</span></>}
                        </div>
                    </Link>
                )}

                {/* Clients */}
                {navigatorData.includes('/manage/clients') && (
                    <Link
                        to="/manage/clients"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Clients' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('manage', 'clients') ? styles.activeNav : ''}`}>
                            <UserCheck size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('manage', 'clients'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Clients</span></>}
                        </div>
                    </Link>
                )}

                {/* Memberships */}
                <Link
                    to="/memberships/home"
                    className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                    title={!isOpen ? 'Memberships' : ''}
                >
                    <div className={`${styles.eachNavContainer} ${checkActivePage('memberships') ? styles.activeNav : ''}`}>
                        <WalletCards size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('memberships'))} />
                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Memberships</span></>}
                    </div>
                </Link>

                {/* Invoices */}
                {navigatorData.includes('/invoices') && (
                    <Link
                        to="/invoices"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Invoices' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('invoices') ? styles.activeNav : ''}`}>
                            <FileText size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('invoices'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Invoices</span></>}
                        </div>
                    </Link>
                )}

                {/* Calendar */}
                {navigatorData.includes('/calendar') && (
                    <Link
                        to="/calendar"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Calendar' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('calendar') ? styles.activeNav : ''}`}>
                            <CalendarDays size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('calendar'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Calendar</span></>}
                        </div>
                    </Link>
                )}

                {/* Price List */}
                {navigatorData.includes('/manage/price-list') && (
                    <Link
                        to="/manage/price-list"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Price List' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('manage', 'price-list') ? styles.activeNav : ''}`}>
                            <ListChecks size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('manage', 'price-list'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Price List</span></>}
                        </div>
                    </Link>
                )}

                {/* Inventory */}
                <Link
                    to="/manage/inventory"
                    className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                    title={!isOpen ? 'Manage Inventory' : ''}
                >
                    <div className={`${styles.eachNavContainer} ${checkActivePage('manage', 'inventory') ? styles.activeNav : ''}`}>
                        <Package size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('manage', 'inventory'))} />
                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Manage Inventory</span></>}
                    </div>
                </Link>

                {/* Locations / Stores */}
                {navigatorData.includes('/manage-stores') && (
                    <Link
                        to="/manage-stores"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Locations / Stores' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('manage-stores') ? styles.activeNav : ''}`}>
                            <Building2 size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('manage-stores'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Locations / Stores</span></>}
                        </div>
                    </Link>
                )}

                {/* Users */}
                {navigatorData.includes('/manage/employees') && (
                    <Link
                        to="/manage/employees"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Users' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('manage', 'employees') ? styles.activeNav : ''}`}>
                            <Users size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('manage', 'employees'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Users</span></>}
                        </div>
                    </Link>
                )}

                {/* Feedbacks */}
                <Link
                    to="/feedbacks"
                    className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                    title={!isOpen ? 'Feedbacks' : ''}
                >
                    <div className={`${styles.eachNavContainer} ${checkActivePage('feedbacks') ? styles.activeNav : ''}`}>
                        <Star size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('feedbacks'))} />
                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Feedbacks</span></>}
                    </div>
                </Link>

                {/* Special Moments */}
                <Link
                    to="/special-moments"
                    className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                    title={!isOpen ? 'Special Moments (New)' : ''}
                >
                    <div className={`${styles.eachNavContainer} ${checkActivePage('special-moments') ? styles.activeNav : ''}`}>
                        <PartyPopper size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('special-moments'))} />
                        {/* Collapsed rail has no room for the text pill below, so a
                            corner dot carries the same "new" signal there instead. */}
                        {!isOpen && <span className={styles.newDot} aria-hidden="true" />}
                        {isOpen && (
                            <>
                                <div className={styles.navIcon} />
                                <span className={styles.navTxt}>Special Moments</span>
                                <span className={styles.newBadge}>NEW</span>
                            </>
                        )}
                    </div>
                </Link>

                {/* Manage Staff */}
                <Link
                    to="/staff"
                    className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                    title={!isOpen ? 'Manage Staff' : ''}
                >
                    <div className={`${styles.eachNavContainer} ${checkActivePage('staff') ? styles.activeNav : ''}`}>
                        <UsersRound size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('staff'))} />
                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Manage Staff</span></>}
                    </div>
                </Link>

                {/* Registration Forms */}
                {navigatorData.includes('/user-registration-forms') && (
                    <Link
                        to="/user-registration-forms"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Registration Forms' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('user-registration-forms') ? styles.activeNav : ''}`}>
                            <ClipboardList size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('user-registration-forms'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Registration Forms</span></>}
                        </div>
                    </Link>
                )}

                {/* LeadBridge */}
                <Link
                    to="/leadbridge"
                    className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                    title={!isOpen ? 'LeadBridge™' : ''}
                >
                    <div className={`${styles.eachNavContainer} ${checkActivePage('leadbridge') ? styles.activeNav : ''}`}>
                        <Link2 size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('leadbridge'))} />
                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>LeadBridge™</span></>}
                    </div>
                </Link>

                {/* ── Promo Section ── */}
                {hasPromoAccess && (
                    <div className={styles.sectionGroup}>
                        {isOpen && (
                            <div className={styles.sectionTitle}>
                                Promo
                                <span className={styles.newBadge}>NEW</span>
                            </div>
                        )}
                        {!isOpen && <div className={styles.navDivider} />}

                        {navigatorData.includes('/communications/creative-studio') && (
                            <Link
                                to="/communications/creative-studio"
                                className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                                title={!isOpen ? 'Creative Studio' : ''}
                            >
                                <div className={`${styles.eachNavContainer} ${checkActivePage('communications', 'creative-studio') ? styles.activeNav : ''}`}>
                                    <Sparkles size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('communications', 'creative-studio'))} />
                                    {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Creative Studio</span></>}
                                </div>
                            </Link>
                        )}

                        {navigatorData.includes('/communications') && (
                            <Link
                                to="/communications"
                                className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                                title={!isOpen ? 'Hub (New)' : ''}
                            >
                                <div className={`${styles.eachNavContainer} ${isExactPath('communications') ? styles.activeNav : ''}`}>
                                    <Megaphone size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(isExactPath('communications'))} />
                                    {!isOpen && <span className={styles.newDot} aria-hidden="true" />}
                                    {isOpen && (
                                        <>
                                            <div className={styles.navIcon} />
                                            <span className={styles.navTxt}>Hub</span>
                                            <span className={styles.newBadge}>NEW</span>
                                        </>
                                    )}
                                </div>
                            </Link>
                        )}

                        {navigatorData.includes('/communications/all-campaigns') && (
                            <Link
                                to="/communications/all-campaigns"
                                className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                                title={!isOpen ? 'Campaigns' : ''}
                            >
                                <div className={`${styles.eachNavContainer} ${isCampaignsSection() ? styles.activeNav : ''}`}>
                                    <Target size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(isCampaignsSection())} />
                                    {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Campaigns</span></>}
                                </div>
                            </Link>
                        )}

                        {navigatorData.includes('/communications/audience/all-audiences') && (
                            <Link
                                to="/communications/audience/all-audiences"
                                className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                                title={!isOpen ? 'Audiences' : ''}
                            >
                                <div className={`${styles.eachNavContainer} ${checkActivePage('communications', 'audience') ? styles.activeNav : ''}`}>
                                    <UsersRound size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('communications', 'audience'))} />
                                    {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Audiences</span></>}
                                </div>
                            </Link>
                        )}
                    </div>
                )}

                {/* ── Reports Section ── */}
                {hasReportsAccess && (
                    <div className={styles.sectionGroup}>
                        {isOpen && <div className={styles.sectionTitle}>Reports</div>}
                        {!isOpen && <div className={styles.navDivider} />}

                        {navigatorData.includes('/collection-reports') && (
                            <Link
                                to="/collection-reports"
                                className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                                title={!isOpen ? 'Collection Reports' : ''}
                            >
                                <div className={`${styles.eachNavContainer} ${checkActivePage('collection-reports') ? styles.activeNav : ''}`}>
                                    <BarChart2 size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('collection-reports'))} />
                                    {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Collection Reports</span></>}
                                </div>
                            </Link>
                        )}

                        {navigatorData.includes('/expenses-report') && (
                            <Link
                                to="/expenses-report"
                                className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                                title={!isOpen ? 'Expenses Report' : ''}
                            >
                                <div className={`${styles.eachNavContainer} ${checkActivePage('expenses-report') ? styles.activeNav : ''}`}>
                                    <TrendingUp size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('expenses-report'))} />
                                    {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Expenses Report</span></>}
                                </div>
                            </Link>
                        )}

                        {navigatorData.includes('/outstanding-report') && (
                            <Link
                                to="/outstanding-report"
                                className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                                title={!isOpen ? 'Outstanding Report' : ''}
                            >
                                <div className={`${styles.eachNavContainer} ${checkActivePage('outstanding-report') ? styles.activeNav : ''}`}>
                                    <CreditCard size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('outstanding-report'))} />
                                    {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Outstanding Report</span></>}
                                </div>
                            </Link>
                        )}
                    </div>
                )}

                {/* ── Payroll Section ── */}
                {isPayrollLaunched &&
                    <div className={styles.sectionGroup}>
                        {isOpen && <div className={styles.sectionTitle}>Payroll</div>}
                        {!isOpen && <div className={styles.navDivider} />}

                        {/* Overview (placeholder) */}
                        <Link
                            to="/payroll/overview"
                            className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                            title={!isOpen ? 'Payroll Overview' : ''}
                        >
                            <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'overview') ? styles.activeNav : ''}`}>
                                <Banknote size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'overview'))} />
                                {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Payroll Overview</span></>}
                            </div>
                        </Link>
                        {/* Munim Ji (placeholder) */}
                        <Link
                            to="/payroll/ask-munim-ji"
                            className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                            title={!isOpen ? 'Ask Munim Ji' : ''}
                        >
                            <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'ask-munim-ji') ? styles.activeNav : ''}`}>
                                <Bot size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'ask-munim-ji'))} />
                                {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Ask Munim Ji</span></>}
                            </div>
                        </Link>

                        {/* Run Payroll */}
                        <Link
                            to="/payroll/run"
                            className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                            title={!isOpen ? 'Run Payroll' : ''}
                        >
                            <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'run') ? styles.activeNav : ''}`}>
                                <RefreshCw size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'run'))} />
                                {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Run Payroll</span></>}
                            </div>
                        </Link>

                        {/* Payroll Run History */}
                        <Link
                            to="/payroll/run-history"
                            className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                            title={!isOpen ? 'Payroll Run History' : ''}
                        >
                            <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'run-history') ? styles.activeNav : ''}`}>
                                <CalendarDays size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'run-history'))} />
                                {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Payroll Run History</span></>}
                            </div>
                        </Link>
                        <Link
                            to="/payroll/advance-book"
                            className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                            title={!isOpen ? 'Advance Book' : ''}
                        >
                            <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'advance-book') ? styles.activeNav : ''}`}>
                                <BookOpen size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'advance-book'))} />
                                {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Advance Book</span></>}
                            </div>
                        </Link>
                        <Link
                            to="/payroll/fnf"
                            className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                            title={!isOpen ? 'FnF' : ''}
                        >
                            <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'fnf') ? styles.activeNav : ''}`}>
                                <UserMinus size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'fnf'))} />
                                {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>FnF</span></>}
                            </div>
                        </Link>
                        {/* Payroll Simulator */}
                        <Link
                            to="/payroll/simulator"
                            className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                            title={!isOpen ? 'Payroll Simulator' : ''}
                        >
                            <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'simulator') ? styles.activeNav : ''}`}>
                                <Sparkles size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'simulator'))} />
                                {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Payroll Simulator</span></>}
                            </div>
                        </Link>


                        {/* ── Setup (collapsible) ── */}
                        {isOpen ? (
                            <div
                                className={styles.setupToggleRow}
                                onClick={() => setPayrollSetupOpen(o => !o)}
                            >
                                <Wrench size={iconSize} strokeWidth={ICON_STROKE} color="rgba(148, 163, 184, 0.8)" />
                                <div className={styles.navIcon} />
                                <span className={styles.navTxt}>Setup</span>
                                <div style={{ flex: 1 }} />
                                {payrollSetupOpen
                                    ? <ChevronDown size={13} strokeWidth={2} color="rgba(148, 163, 184, 0.6)" />
                                    : <ChevronRight size={13} strokeWidth={2} color="rgba(148, 163, 184, 0.6)" />}
                            </div>
                        ) : (
                            <div className={styles.navDivider} />
                        )}

                        {(payrollSetupOpen || !isOpen) && (
                            <>
                                <Link
                                    to="/payroll/setup"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Payroll Setup' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'setup') ? styles.activeNav : ''}`}>
                                        <SlidersHorizontal size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'setup'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Payroll Setup</span></>}
                                    </div>
                                </Link>

                                <Link
                                    to="/payroll/setup/cycle-config/view"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Cycle Config' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'setup', 'cycle-config') ? styles.activeNav : ''}`}>
                                        <RefreshCw size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'setup', 'cycle-config'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Cycle Config</span></>}
                                    </div>
                                </Link>

                                <Link
                                    to="/payroll/setup/compliance-config"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Compliance Config' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'setup', 'compliance-config') ? styles.activeNav : ''}`}>
                                        <Shield size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'setup', 'compliance-config'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Compliance Config</span></>}
                                    </div>
                                </Link>

                                <Link
                                    to="/payroll/salary-structure"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Salary Structures' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'salary-structure') ? styles.activeNav : ''}`}>
                                        <Layers size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'salary-structure'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Salary Structures</span></>}
                                    </div>
                                </Link>

                                <Link
                                    to="/payroll/setup/incentive-structure"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Incentive Structures' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'setup', 'incentive-structure') ? styles.activeNav : ''}`}>
                                        <Gift size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'setup', 'incentive-structure'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Incentive Structures</span></>}
                                    </div>
                                </Link>

                                <Link
                                    to="/payroll/setup/holiday-master"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Holiday Master' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'setup', 'holiday-master') ? styles.activeNav : ''}`}>
                                        <Umbrella size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'setup', 'holiday-master'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Holiday Master</span></>}
                                    </div>
                                </Link>

                                <Link
                                    to="/payroll/setup/leave-policy"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Leave Policy' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'setup', 'leave-policy') ? styles.activeNav : ''}`}>
                                        <CalendarCheck size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'setup', 'leave-policy'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Leave Policy</span></>}
                                    </div>
                                </Link>


                                {/* ── Staff sub-header ── */}
                                {isOpen && <div className={styles.subSectionLabel}>── STAFF ──</div>}

                                <Link
                                    to="/payroll/staff-readiness"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Staff Readiness' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'staff-readiness') ? styles.activeNav : ''}`}>
                                        <UserCog size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'staff-readiness'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Staff Readiness</span></>}
                                    </div>
                                </Link>

                                <Link
                                    to="/payroll/staff-assignments"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Staff Assignments' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'staff-assignments') ? styles.activeNav : ''}`}>
                                        <Users size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'staff-assignments'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Staff Assignments</span></>}
                                    </div>
                                </Link>

                                {/* ── Advances sub-header ── */}
                                {/* {isOpen && <div className={styles.subSectionLabel}>── ADVANCES ──</div>} */}



                                {/* ── Leave sub-header ── */}
                                {isOpen && <div className={styles.subSectionLabel}>── LEAVE ──</div>}

                                <Link
                                    to="/payroll/leave-balance-overview"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Leave Overview' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'leave-balance-overview') ? styles.activeNav : ''}`}>
                                        <CalendarCheck size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'leave-balance-overview'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Leave Overview</span></>}
                                    </div>
                                </Link>

                                <Link
                                    to="/payroll/year-end-leave-process"
                                    className={isOpen ? styles.eachNavWrapperIndent : styles.eachNavWrapperSmall}
                                    title={!isOpen ? 'Year-End Leave' : ''}
                                >
                                    <div className={`${styles.eachNavContainer} ${checkActivePage('payroll', 'year-end-leave-process') ? styles.activeNav : ''}`}>
                                        <CalendarX2 size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('payroll', 'year-end-leave-process'))} />
                                        {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Year-End Leave</span></>}
                                    </div>
                                </Link>

                            </>
                        )}
                    </div>
                }

                {/* ── Analytics Section ── */}
                {hasAnalyticsAccess && (
                    <div className={styles.sectionGroup}>
                        {isOpen && <div className={styles.sectionTitle}>Analytics</div>}
                        {!isOpen && <div className={styles.navDivider} />}

                        {navigatorData.includes('/analytics/staff') && (
                            <Link
                                to="/analytics/staff"
                                className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                                title={!isOpen ? 'Staff Analytics' : ''}
                            >
                                <div className={`${styles.eachNavContainer} ${checkActivePage('analytics', 'staff') ? styles.activeNav : ''}`}>
                                    <Users size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('analytics', 'staff'))} />
                                    {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Staff Analytics</span></>}
                                </div>
                            </Link>
                        )}
                    </div>
                )}


                {/* ── Settings Section ── */}
                <div className={styles.sectionGroup}>
                    {isOpen && <div className={styles.sectionTitle}>Settings</div>}
                    {!isOpen && <div className={styles.navDivider} />}

                    <Link
                        to="/settings/set-up"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Account Set-up' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('settings', 'set-up') ? styles.activeNav : ''}`}>
                            <SlidersHorizontal size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('settings', 'set-up'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Account Set-up</span></>}
                        </div>
                    </Link>

                    <Link
                        to="/settings/account"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Account Setting' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('settings', 'account') ? styles.activeNav : ''}`}>
                            <Settings2 size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('settings', 'account'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Account Setting</span></>}
                        </div>
                    </Link>

                    <Link
                        to="/settings/security"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Security Setting' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('settings', 'security') ? styles.activeNav : ''}`}>
                            <ShieldCheck size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('settings', 'security'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Security Setting</span></>}
                        </div>
                    </Link>

                    <Link
                        to="/settings/automation"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Automation Setting' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('settings', 'automation') ? styles.activeNav : ''}`}>
                            <Zap size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('settings', 'automation'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Automation Setting</span></>}
                        </div>
                    </Link>

                    <Link
                        to="/reports/daily/settings"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Daily Reports Settings' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('reports', 'daily', 'settings') ? styles.activeNav : ''}`}>
                            <Settings2 size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('reports', 'daily', 'settings'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Daily Reports</span></>}
                        </div>
                    </Link>

                    <Link
                        to="/settings/notifications"
                        className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall}
                        title={!isOpen ? 'Notifications Setting' : ''}
                    >
                        <div className={`${styles.eachNavContainer} ${checkActivePage('settings', 'notifications') ? styles.activeNav : ''}`}>
                            <Bell size={iconSize} strokeWidth={ICON_STROKE} color={iconColor(checkActivePage('settings', 'notifications'))} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Notifications</span></>}
                        </div>
                    </Link>
                </div>


            </div>

            {/* ── Footer: Change Store + Logout ── */}
            <div className={styles.hfWrapper}>

                <div
                    className={styles.changeActiveStore}
                    onClick={() => setIsStoreSelectorOpen(true)}
                >
                    <div className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall} title={!isOpen ? 'Change Store' : ''}>
                        <div className={styles.eachNavContainer}>
                            <ArrowLeftRight size={iconSize} strokeWidth={ICON_STROKE} color="rgba(148, 163, 184, 0.7)" />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Change Store</span></>}
                        </div>
                    </div>
                </div>

                <div
                    className={styles.logoutRow}
                    onClick={() => authCtx.logout()}
                >
                    <div className={isOpen ? styles.eachNavWrapper : styles.eachNavWrapperSmall} title={!isOpen ? 'Logout' : ''}>
                        <div className={styles.eachNavContainer}>
                            <LogOut size={iconSize} strokeWidth={ICON_STROKE} />
                            {isOpen && <><div className={styles.navIcon} /><span className={styles.navTxt}>Logout</span></>}
                        </div>
                    </div>
                </div>

            </div>

        </div>
        </>
    )
}

export default LeftMenu
